import React from 'react'
import Link from 'next/link'
import { MenuItemType } from '@next-nx/utils/types'
import { useFindMenu, useTealium } from '@next-nx/hooks'
import { getAnchorRel, getTimestamp } from '@next-nx/utils'
import { getIcon } from '@next-nx/utils/icons'
import { ArrowRight } from '@next-nx/shared-ui/icons'
import { Button, IconBg, WContainer } from '@next-nx/shared-ui/atoms'
import { CircleBoxSmall, OffertaCard } from '@next-nx/shared-ui/molecules'
import styles from './NavDropdown.module.sass'

interface Props {
  item: MenuItemType
  open: boolean
  className?: string
  setopenPreventivoModal?: (v: boolean) => void
}

const NavDropdown = (props: Props) => {
  const {
    item = undefined,
    open = false,
    className = '',
    setopenPreventivoModal = () => {},
  } = props

  const soluzioni = useFindMenu('soluzioni', item)
  const soluzioni_testo = useFindMenu('soluzioni-testo', item)
  const link = useFindMenu('link', item)
  const faq = useFindMenu('faq', item)
  const prodotti = useFindMenu('prodotti', item)
  const boxSmall = useFindMenu('box-small', item)

  const { linkDataLayer } = useTealium()

  const handleClick = (action_type = '', cta = '-', info: string | undefined = '') => {
    const action_timestamp = getTimestamp()

    linkDataLayer({
      virtual: 'action',
      action_name: 'menu',
      action_tech: 'click',
      action_type: action_type.replace(/\.|\/|\s+/g, '_').toLocaleLowerCase(),
      action_timestamp,
      action_effect: 'navigazione_menu',
      cta: cta.replace(/\.|\/|\s+/g, '_').toLowerCase(),
      info: info?.replace(/\.|\/|\s+/g, '_').toLowerCase(),
    })
  }

  return item?.children?.edges.length ? (
    <div className={`${styles.root} ${className} ${open ? styles['root--open'] : ''}`}>
      <WContainer variant="lg">
        <div className={styles.layout}>
          {!!soluzioni?.children?.edges.length && (
            <div className={styles.col1}>
              <div className={`${styles.block}`}>
                <p className={styles.blockTitle}>{soluzioni.name}</p>
                <div className={styles.soluzioniList}>
                  {soluzioni.children.edges.map((x, i) => (
                    <Link href={x.node.url || ''} key={x.node.pk}>
                      <a
                        className={styles.soluzioniItem}
                        rel={getAnchorRel(x.node.url || '')}
                        onClick={() => handleClick(item.name, soluzioni.name, x.node.name)}
                      >
                        <IconBg
                          icon={getIcon(x.node.style)}
                          animationVariant={i % 2 === 0 ? 'se' : 'nw'}
                          className={styles.soluzioniItemIcon}
                          bgClassName={styles.soluzioniIconBg}
                        />
                        <span className={styles.soluzioniItemLabel}>{x.node.name}</span>
                      </a>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
          {!!soluzioni_testo && (
            <div className={styles.col1}>
              <div className={`${styles.block}`}>
                <p className={styles.blockTitle}>{soluzioni_testo?.name}</p>
                <p className={styles.soluzioniDesc}>{soluzioni_testo?.description}</p>
                <Button
                  label={soluzioni_testo?.ctaLabel}
                  href={soluzioni_testo?.url}
                  variant={'ghost'}
                  iconPos={'right'}
                  icon={<ArrowRight />}
                  onClick={() =>
                    handleClick(item.name, soluzioni_testo?.name, soluzioni_testo?.ctaLabel)
                  }
                />
              </div>
            </div>
          )}
          {!!(link?.children?.edges?.length || faq?.children?.edges?.length) && (
            <div className={styles.col2}>
              {!!link?.children?.edges?.length && (
                <div className={`${styles.block}`}>
                  <p className={styles.blockTitle}>{link.name}</p>
                  <div className={styles.linkList}>
                    {link.children.edges.map((x) => (
                      <Link href={x.node.url || '#'} key={x.node.pk}>
                        <a
                          className={styles.linkItem}
                          rel={getAnchorRel(x.node.url || '')}
                          onClick={() => handleClick(item.name, link.name, x.node.name)}
                        >
                          <span className={styles.linkItemLabel}>{x.node.name}</span>
                          <span className={styles.linkItemIcon}>
                            <ArrowRight />
                          </span>
                        </a>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {!!faq?.children?.edges.length && (
                <div className={`${styles.block}`}>
                  <p className={styles.blockTitle}>{faq.name}</p>
                  <div className={styles.faqList}>
                    {faq.children.edges.map((x) => (
                      <Link href={x.node.url || ''} key={x.node.pk}>
                        <a
                          className={styles.faqItem}
                          rel={getAnchorRel(x.node.url || '')}
                          onClick={() =>
                            handleClick(item.name, faq.name, x.node.faq?.domanda || x.node.name)
                          }
                        >
                          {x.node.faq?.domanda || x.node.name}
                        </a>
                      </Link>
                    ))}
                  </div>
                  {!!faq.url && !!faq.ctaLabel && (
                    <div className={styles.blockCtaBox}>
                      <Button
                        variant="ghost"
                        icon={<ArrowRight />}
                        iconPos="right"
                        label={faq.ctaLabel}
                        href={faq.url}
                        onClick={() => handleClick(item.name, faq.name, faq.ctaLabel)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {!!prodotti?.children?.edges.length && (
            <div className={styles.col3}>
              {!!prodotti.children.edges.some((c) => !!c.node.offerta) && (
                <div className={`${styles.block}`}>
                  <p className={styles.blockTitle}>{prodotti.name}</p>
                  <div className={styles.prodottiList}>
                    {prodotti.children?.edges.map(
                      (x) =>
                        x.node.offerta && (
                          <OffertaCard
                            offerta={x.node.offerta}
                            codiceCategoriaPrimaria={item.key}
                            key={x.node.pk}
                            azione="menu"
                            sezione={prodotti.name?.split('per')[1].trim().toLowerCase()}
                          />
                        )
                    )}
                  </div>
                  {!!prodotti.url && !!prodotti.ctaLabel && (
                    <div className={styles.blockCtaBox}>
                      <Button
                        variant="ghost"
                        icon={<ArrowRight />}
                        iconPos="right"
                        label={prodotti.ctaLabel}
                        href={prodotti.url}
                        onClick={() => handleClick(item.name, prodotti.name, prodotti.ctaLabel)}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {!!boxSmall && (
            <div className={styles.col3}>
              <div className={`${styles.block}`}>
                <CircleBoxSmall
                  handleTracking={() => handleClick(item?.name, '-', boxSmall?.description)}
                  item={boxSmall}
                  setOpen={() => {
                    setopenPreventivoModal(true)
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </WContainer>
    </div>
  ) : (
    <></>
  )
}

export default NavDropdown
